import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

import { AdminTopbar } from "@/components/admin/AdminTopbar";
import { AdminCard, ListState, StatCard } from "@/components/admin/primitives";
import { formatDate } from "@/lib/dates";
import { formatBRL } from "@/lib/format";
import { COMMISSION_HOLD_DAYS, usePayables } from "@/lib/resellers";
import { useStoreSettings } from "@/lib/settings";
import { cn } from "@/lib/utils";

/**
 * Repasses: quanto cada revendedor tem a receber.
 *
 * Só o resumo — o repasse em si é feito na tela do revendedor, onde dá para
 * escolher as comissões, registrar o Pix e anexar o comprovante.
 */
export const Route = createFileRoute("/admin/_painel/repasses")({
  head: () => ({ meta: [{ title: "Repasses — Painel MonteBox" }] }),
  component: AdminPayouts,
});

const COLUMNS =
  "min-w-[900px] grid grid-cols-[1.4fr_1fr_140px_140px_140px_150px_24px] items-center gap-4";

function AdminPayouts() {
  const payables = usePayables();
  const settings = useStoreSettings().data;
  const locked = settings?.payoutsLocked ?? false;
  const rows = payables.data ?? [];
  const sum = (key: "pending" | "released" | "paid") =>
    rows.reduce((total, row) => total + row[key], 0);

  return (
    <>
      <AdminTopbar title="Repasses" />
      <div className="flex flex-col gap-6 p-4 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-3 sm:gap-6">
          <StatCard label="A pagar (liberado)" value={formatBRL(sum("released"))} />
          <StatCard label="A liberar" value={formatBRL(sum("pending"))} />
          <StatCard label="Já repassado" value={formatBRL(sum("paid"))} />
        </div>

        {locked ? (
          <p className="rounded-mb-field border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            <span className="font-bold">Repasses travados.</span> As comissões continuam sendo
            geradas e liberadas, mas nenhum repasse sai até destravar em{" "}
            <Link to="/admin/configuracoes" className="font-bold underline">
              Configurações
            </Link>
            .
          </p>
        ) : (
          <p className="text-xs text-mb-muted">
            A comissão libera {COMMISSION_HOLD_DAYS} dias depois de o pedido ser concluído e vai
            automaticamente pelo Mercado Pago para a conta do parceiro
            {settings
              ? `, ${settings.payment.payoutExtraDays} dias depois da liberação do dinheiro`
              : ""}
            . Aqui você acompanha o saldo de cada um.
          </p>
        )}

        <AdminCard className="overflow-x-auto">
          <div
            className={cn(
              COLUMNS,
              "bg-mb-surface px-6 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-mb-muted",
            )}
          >
            <span>Revendedor</span>
            <span>Pix</span>
            <span className="text-right">A pagar</span>
            <span className="text-right">A liberar</span>
            <span className="text-right">Pago</span>
            <span className="text-right">Próxima liberação</span>
            <span />
          </div>
          <ListState
            loading={payables.isLoading}
            error={payables.error}
            empty={rows.length === 0}
            emptyLabel="Nenhum revendedor com comissão."
          />
          {rows.map((row) => (
            <Link
              key={row.resellerId}
              to="/admin/equipe/$membroId"
              params={{ membroId: row.userId }}
              className={cn(
                COLUMNS,
                "border-t border-mb-line px-6 py-3.5 text-sm tabular-nums transition-colors hover:bg-mb-surface/60",
              )}
            >
              <span className="min-w-0">
                <span className="block truncate font-semibold text-mb-ink">{row.name}</span>
                {!row.active && (
                  <span className="text-[11px] font-bold text-mb-muted">acesso desativado</span>
                )}
              </span>
              <span className="truncate text-xs text-mb-muted">
                {row.pixKey ?? <span className="text-destructive">sem chave Pix</span>}
              </span>
              <span
                className={cn(
                  "text-right font-display font-bold",
                  row.released > 0 ? "text-mb-navy" : "text-mb-muted",
                )}
              >
                {formatBRL(row.released)}
              </span>
              <span className="text-right text-mb-muted">{formatBRL(row.pending)}</span>
              <span className="text-right text-mb-muted">{formatBRL(row.paid)}</span>
              <span className="text-right text-xs text-mb-muted">
                {row.nextReleaseAt ? formatDate(row.nextReleaseAt) : "—"}
              </span>
              <ChevronRight className="h-4 w-4 text-mb-muted" />
            </Link>
          ))}
        </AdminCard>
      </div>
    </>
  );
}
